import {
  BackSide,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  FrontSide,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  SRGBColorSpace,
  Vector3,
} from 'three';

/**
 * Peleryna z materiału: siatka punktów (Verlet) przypięta do barków, opada pod własnym ciężarem, zostaje w tyle
 * w biegu i nie wchodzi w plecy ani nogi (kapsuły na kościach). Zastępuje sztywne modele płaszczy, które na
 * nowych ciałach odstawały od pleców jak żagiel.
 */
export interface CapeStyle {
  /** Wierzch, podszewka i lamówka. */
  color: string;
  lining: string;
  trim: string;
  /** Długość w długościach tułowia (biodra–szyja). */
  length: number;
  /** Szerokość dołu względem szerokości barków. */
  flare: number;
  /** Postrzępiony dół: 0 = równy, 1 = mocno poszarpany. */
  ragged?: number;
  pattern?: 'herb' | 'gwiazdy' | 'futro';
}

export const CAPES: Record<string, CapeStyle> = {
  'peleryna-rycerska': { color: '#7c2226', lining: '#3b2b22', trim: '#c9a24a', length: 1.75, flare: 1.35, pattern: 'herb' },
  'skora-wilka': { color: '#5c4a38', lining: '#4a3526', trim: '#3d3329', length: 1.25, flare: 1.15, ragged: 0.6, pattern: 'futro' },
  'plaszcz-cienia': { color: '#262c3d', lining: '#16181f', trim: '#56627e', length: 1.8, flare: 1.4, ragged: 0.2 },
  'plaszcz-gwiazd': { color: '#22336e', lining: '#2b2150', trim: '#d9dcef', length: 1.85, flare: 1.45, pattern: 'gwiazdy' },
  'postrzepiona-peleryna': { color: '#3d3a27', lining: '#2f3424', trim: '#6e5d3c', length: 1.55, flare: 1.3, ragged: 1 },
};

export function capeStyle(visual: string): CapeStyle | undefined {
  return visual.startsWith('item:') ? CAPES[visual.slice(5)] : undefined;
}

/** Kości, na których wisi peleryna (nazwy po sanityzacji GLTFLoadera). */
const BONES = {
  shoulderL: 'DEF-upper_armL',
  shoulderR: 'DEF-upper_armR',
  neck: 'DEF-neck',
  chest: 'DEF-spine003',
  hips: 'DEF-hips',
  thighL: 'DEF-thighL',
  thighR: 'DEF-thighR',
  shinL: 'DEF-shinL',
  shinR: 'DEF-shinR',
  footL: 'DEF-footL',
  footR: 'DEF-footR',
};

const NX = 13;
const NY = 16;
const ITERATIONS = 8;
const STEP = 1 / 60;

interface Capsule {
  a: Vector3;
  b: Vector3;
  r: number;
}

const tmpA = new Vector3();
const tmpB = new Vector3();
const tmpC = new Vector3();
const tmpUp = new Vector3(0, 1, 0);
const tmpV = new Vector3();

export class Cape {
  readonly object = new Object3D();
  private readonly geometry = new BufferGeometry();
  private readonly pos = Array.from({ length: NX * NY }, () => new Vector3());
  private readonly prev = Array.from({ length: NX * NY }, () => new Vector3());
  private readonly links: [number, number, number][] = [];
  private readonly bones: Record<keyof typeof BONES, Object3D>;
  private readonly capsules: Capsule[] = [];
  private readonly anchors = Array.from({ length: NX }, () => new Vector3());
  private readonly side = new Vector3();
  private readonly up = new Vector3();
  private readonly back = new Vector3();
  /** Ruch korzenia postaci na jeden krok symulacji (tłumienie liczymy względem bohatera, nie świata). */
  private readonly carry = new Vector3();
  private readonly lastRoot = new Vector3();
  /** Długość tułowia w świecie — skala wszystkich odległości. */
  private unit = 0;
  private started = false;
  private acc = 0;
  private time = Math.random() * 10;

  /** Null, gdy szkielet nie ma potrzebnych kości (wtedy zostaje stary model płaszcza). */
  static create(style: CapeStyle, tier: number, rarity: string, bone: (name: string) => Object3D | undefined): Cape | null {
    const found: Partial<Record<keyof typeof BONES, Object3D>> = {};
    for (const [key, name] of Object.entries(BONES)) {
      const b = bone(name);
      if (!b) return null;
      found[key as keyof typeof BONES] = b;
    }
    return new Cape(style, tier, rarity, found as Record<keyof typeof BONES, Object3D>);
  }

  private constructor(private readonly style: CapeStyle, tier: number, rarity: string, bones: Record<keyof typeof BONES, Object3D>) {
    this.bones = bones;
    const uv = new Float32Array(NX * NY * 2);
    const index: number[] = [];
    for (let j = 0; j < NY; j++) {
      for (let i = 0; i < NX; i++) {
        uv[(j * NX + i) * 2] = i / (NX - 1);
        uv[(j * NX + i) * 2 + 1] = 1 - j / (NY - 1);
        if (i < NX - 1 && j < NY - 1) {
          const a = j * NX + i;
          // Kolejność wierzchołków: przednia ściana patrzy od pleców na zewnątrz.
          index.push(a, a + NX, a + 1, a + 1, a + NX, a + NX + 1);
        }
      }
    }
    this.geometry.setAttribute('position', new BufferAttribute(new Float32Array(NX * NY * 3), 3));
    this.geometry.setAttribute('normal', new BufferAttribute(new Float32Array(NX * NY * 3), 3));
    this.geometry.setAttribute('uv', new BufferAttribute(uv, 2));
    this.geometry.setIndex(index);

    const map = capeTexture(style);
    const glow = new Color(rarity);
    // Tkanina matowa; rzadkość tylko jako ledwie widoczny poblask (mocniejszy farbował płaszcz na kolor rzadkości).
    const outer = new MeshStandardMaterial({ map, roughness: 1, metalness: 0, side: FrontSide, alphaTest: 0.5 });
    outer.emissive = glow;
    outer.emissiveIntensity = [0, 0, 0.012, 0.03][Math.min(tier, 3)]!;
    const lining = capeTexture({ ...style, color: style.lining, pattern: undefined });
    const inner = new MeshStandardMaterial({ map: lining, roughness: 1, side: BackSide, alphaTest: 0.5 });
    for (const material of [outer, inner]) {
      const mesh = new Mesh(this.geometry, material);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      this.object.add(mesh);
    }
    this.object.name = 'peleryna';
    this.capsules = Array.from({ length: 7 }, () => ({ a: new Vector3(), b: new Vector3(), r: 0 }));
  }

  /** Wołać po animacji szkieletu, raz na klatkę. Obiekt peleryny musi wisieć w korzeniu postaci. */
  update(dt: number): void {
    const root = this.object.parent;
    if (!root) return;
    root.updateMatrixWorld(true);
    this.frame();
    const teleported = this.started && this.pos[0]!.distanceTo(this.anchors[0]!) > this.unit * 3;
    if (!this.started || teleported) this.drape();
    this.acc = Math.min(this.acc + dt, STEP * 4);
    const rootPos = root.getWorldPosition(tmpA);
    const steps = Math.floor(this.acc / STEP);
    this.carry.subVectors(rootPos, this.lastRoot).divideScalar(Math.max(1, steps));
    if (teleported || this.carry.length() > this.unit) this.carry.set(0, 0, 0);
    this.lastRoot.copy(rootPos);
    while (this.acc >= STEP) {
      this.acc -= STEP;
      this.time += STEP;
      this.step();
    }
    this.write(root);
  }

  /** Barki, osie tułowia i kapsuły ciała w bieżącej pozie (w świecie). */
  private frame(): void {
    const b = this.bones;
    const w = (o: Object3D, v: Vector3) => o.getWorldPosition(v);
    const shL = w(b.shoulderL, tmpA);
    const shR = w(b.shoulderR, tmpB);
    const neck = w(b.neck, tmpC);
    const hips = w(b.hips, new Vector3());
    const chest = w(b.chest, new Vector3());
    this.unit = neck.distanceTo(hips) || 0.6;
    const L = this.unit;
    this.side.subVectors(shR, shL);
    const width = this.side.length() * 1.1;
    this.side.normalize();
    // Pion tułowia z bioder do szyi, w połowie wyprostowany — pochylenie w biegu i przy strzale nie kładzie peleryny.
    this.up.subVectors(neck, hips).normalize().add(tmpUp).normalize();
    this.back.crossVectors(this.side, this.up).normalize();
    this.up.crossVectors(this.back, this.side).normalize();

    // Górna krawędź: łuk za karkiem, końce na barkach.
    const mid = new Vector3().addVectors(shL, shR).multiplyScalar(0.5);
    for (let i = 0; i < NX; i++) {
      const u = i / (NX - 1);
      const arc = Math.sin(Math.PI * u);
      this.anchors[i]!
        .copy(mid)
        .addScaledVector(this.side, (u - 0.5) * width)
        .addScaledVector(this.back, L * (0.02 + 0.14 * arc))
        .addScaledVector(this.up, L * (0.07 - 0.05 * arc));
    }

    // Ciało: dwie kapsuły tułowia (szerszy niż głęboki), pośladki, uda i łydki.
    const c = this.capsules;
    const set = (k: number, a: Vector3, bb: Vector3, r: number) => {
      c[k]!.a.copy(a);
      c[k]!.b.copy(bb);
      c[k]!.r = r * L;
    };
    const off = this.side.clone().multiplyScalar(0.11 * L);
    set(0, hips.clone().add(off), chest.clone().add(off), 0.22);
    set(1, hips.clone().sub(off), chest.clone().sub(off), 0.22);
    const thL = w(b.thighL, new Vector3());
    const thR = w(b.thighR, new Vector3());
    set(2, thL, thR, 0.24);
    const knL = w(b.shinL, new Vector3());
    const knR = w(b.shinR, new Vector3());
    set(3, thL, knL, 0.17);
    set(4, thR, knR, 0.17);
    set(5, knL, w(b.footL, new Vector3()), 0.13);
    set(6, knR, w(b.footR, new Vector3()), 0.13);
  }

  /** Peleryna od razu wisi swobodnie za plecami (pierwsza klatka, skok postaci). */
  private drape(): void {
    this.started = true;
    const seg = (this.style.length * this.unit) / (NY - 1);
    for (let j = 0; j < NY; j++) {
      for (let i = 0; i < NX; i++) {
        const p = this.pos[j * NX + i]!;
        // Dół szerszy niż barki, z falą do tyłu i do przodu — z niej powstają fałdy.
        const t = j / (NY - 1);
        const centre = this.anchors[(NX - 1) / 2]!;
        p.copy(this.anchors[i]!)
          .sub(centre)
          .multiplyScalar(1 + (this.style.flare - 1) * t)
          .add(centre)
          .addScaledVector(this.up, -seg * j)
          .addScaledVector(this.back, seg * j * 0.15 + Math.cos(i * Math.PI * 0.75) * this.unit * 0.06 * t);
        this.prev[j * NX + i]!.copy(p);
      }
    }
    this.buildLinks();
  }

  /** Więzy odległości: sąsiedzi (struktura), przekątne (ścinanie) i co drugi punkt (sztywność). */
  private buildLinks(): void {
    this.links.length = 0;
    const L = this.unit;
    const seg = (this.style.length * L) / (NY - 1);
    const top = this.anchors[0]!.distanceTo(this.anchors[NX - 1]!);
    const dx = (j: number) => (top * (1 + (this.style.flare - 1) * (j / (NY - 1)))) / (NX - 1);
    const add = (i1: number, j1: number, i2: number, j2: number) => {
      const h = Math.abs(i2 - i1) * (dx(j1) + dx(j2)) * 0.5;
      const v = Math.abs(j2 - j1) * seg;
      this.links.push([j1 * NX + i1, j2 * NX + i2, Math.hypot(h, v)]);
    };
    for (let j = 0; j < NY; j++) {
      for (let i = 0; i < NX; i++) {
        if (i < NX - 1) add(i, j, i + 1, j);
        if (j < NY - 1) add(i, j, i, j + 1);
        if (i < NX - 1 && j < NY - 1) {
          add(i, j, i + 1, j + 1);
          add(i + 1, j, i, j + 1);
        }
        if (i < NX - 2) add(i, j, i + 2, j);
        if (j < NY - 2) add(i, j, i, j + 2);
      }
    }
  }

  private step(): void {
    const L = this.unit;
    const gravity = -9.8 * 0.55 * L * STEP * STEP;
    // Lekki, zmienny powiew — żeby peleryna w miejscu nie wisiała martwo.
    const breeze = Math.sin(this.time * 1.3) * 0.5 + Math.sin(this.time * 2.9) * 0.25;
    const wind = this.back.clone().multiplyScalar(0.35 * L * STEP * STEP * (1 + breeze));
    for (let k = NX; k < this.pos.length; k++) {
      const p = this.pos[k]!;
      const q = this.prev[k]!;
      // Kołysanie względem bohatera gaśnie szybko, a opór powietrza przy biegu jest słaby — peleryna
      // odchyla się w tył o kilkadziesiąt stopni, nie fruwa poziomo.
      tmpV.subVectors(p, q).sub(this.carry).multiplyScalar(0.88).add(this.carry).multiplyScalar(0.988);
      q.copy(p);
      p.add(tmpV).add(wind);
      p.y += gravity;
    }
    for (let i = 0; i < NX; i++) {
      this.pos[i]!.copy(this.anchors[i]!);
      this.prev[i]!.copy(this.anchors[i]!);
    }
    const pinned = (k: number) => k < NX;
    for (let it = 0; it < ITERATIONS; it++) {
      for (const [a, b, rest] of this.links) {
        const pa = this.pos[a]!;
        const pb = this.pos[b]!;
        tmpA.subVectors(pb, pa);
        const d = tmpA.length();
        if (d < 1e-6) continue;
        // Tkanina nie rozciąga się ani nie kurczy w płaszczyźnie — nadmiar szerokości dołu idzie w fałdy.
        const diff = (d - rest) / d;
        const k = 1;
        const fa = pinned(a) ? 0 : pinned(b) ? 1 : 0.5;
        const fb = pinned(b) ? 0 : pinned(a) ? 1 : 0.5;
        pa.addScaledVector(tmpA, diff * k * fa);
        pb.addScaledVector(tmpA, -diff * k * fb);
      }
      this.collide();
    }
  }

  private collide(): void {
    const margin = 0.03 * this.unit;
    const seg = (this.style.length * this.unit) / (NY - 1);
    for (let k = NX; k < this.pos.length; k++) {
      const p = this.pos[k]!;
      // Nigdy wyżej niż ok. 70° od pionu (bez tego szarpnięcie przy zwrocie przerzucało pelerynę nad głowę).
      const a = this.anchors[k % NX]!;
      const top = a.y - Math.floor(k / NX) * seg * 0.35;
      if (p.y > top) p.y = top;
      for (const c of this.capsules) {
        tmpB.subVectors(c.b, c.a);
        const len2 = tmpB.lengthSq() || 1;
        const t = Math.max(0, Math.min(1, tmpC.subVectors(p, c.a).dot(tmpB) / len2));
        tmpC.copy(c.a).addScaledVector(tmpB, t);
        tmpA.subVectors(p, tmpC);
        const d = tmpA.length();
        const r = c.r + margin;
        if (d >= r) continue;
        // Punkt w środku kości: wypychamy do tyłu.
        if (d < 1e-5) tmpA.copy(this.back);
        else tmpA.multiplyScalar(1 / d);
        p.copy(tmpC).addScaledVector(tmpA, r);
      }
    }
  }

  private write(root: Object3D): void {
    const attr = this.geometry.getAttribute('position') as BufferAttribute;
    for (let k = 0; k < this.pos.length; k++) {
      // Fałdy: pionowe garby od karku w dół, coraz głębsze ku dołowi (tylko w siatce, fizyka liczy gładki płat).
      const u = (k % NX) / (NX - 1);
      const t = Math.floor(k / NX) / (NY - 1);
      const fold = Math.sin(u * Math.PI * 5 + 0.6) * Math.sin(u * Math.PI) * this.unit * 0.11 * Math.sqrt(t);
      tmpA.copy(this.pos[k]!).addScaledVector(this.back, fold);
      root.worldToLocal(tmpA);
      attr.setXYZ(k, tmpA.x, tmpA.y, tmpA.z);
    }
    attr.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingSphere();
  }

  dispose(): void {
    this.geometry.dispose();
    this.object.traverse((o) => {
      const m = (o as Mesh).material as MeshStandardMaterial | undefined;
      m?.map?.dispose();
      m?.dispose();
    });
    this.object.removeFromParent();
  }
}

/** Tkanina malowana na płótnie: splot, lamówka przy brzegach, wzór i postrzępiony dół (kanał alfa). */
function capeTexture(style: CapeStyle): CanvasTexture {
  const W = 256;
  const H = 512;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  let seed = [...style.color].reduce((s, ch) => s * 31 + ch.charCodeAt(0), 7) >>> 0;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);

  // Tło z delikatnym pionowym gradientem (fałdy u góry ciemniejsze).
  const base = new Color(style.color);
  const grad = g.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, `#${base.clone().multiplyScalar(0.8).getHexString()}`);
  grad.addColorStop(0.4, `#${base.getHexString()}`);
  grad.addColorStop(1, `#${base.clone().multiplyScalar(0.9).getHexString()}`);
  g.fillStyle = grad;
  g.fillRect(0, 0, W, H);

  // Nierówny splot: miękkie, szerokie smugi zamiast nitek (nitki z bliska dawały prążki).
  for (let n = 0; n < 60; n++) {
    const x = rnd() * W;
    g.globalAlpha = 0.03 + rnd() * 0.04;
    g.fillStyle = rnd() > 0.5 ? '#000' : '#fff';
    g.fillRect(x, 0, 3 + rnd() * 10, H);
  }
  g.globalAlpha = 1;

  if (style.pattern === 'futro') {
    // Pasma sierści: krótkie, wygięte pociągnięcia jaśniejsze i ciemniejsze od tła.
    for (let n = 0; n < 2600; n++) {
      const x = rnd() * W;
      const y = rnd() * H;
      const l = 6 + rnd() * 14;
      g.strokeStyle = `#${base.clone().multiplyScalar(0.6 + rnd() * 0.7).getHexString()}`;
      g.globalAlpha = 0.5;
      g.lineWidth = 1 + rnd() * 1.5;
      g.beginPath();
      g.moveTo(x, y);
      g.quadraticCurveTo(x + (rnd() - 0.5) * 6, y + l * 0.5, x + (rnd() - 0.5) * 8, y + l);
      g.stroke();
    }
    g.globalAlpha = 1;
  }

  const trim = new Color(style.trim);
  const trimHex = `#${trim.getHexString()}`;
  if (style.pattern !== 'futro') {
    // Lamówka: boki i podwójny pas przy dole.
    g.fillStyle = trimHex;
    g.fillRect(0, 0, 7, H);
    g.fillRect(W - 7, 0, 7, H);
    if (!style.ragged || style.ragged < 0.5) {
      g.fillRect(0, H - 34, W, 9);
      g.fillRect(0, H - 20, W, 3);
    }
  }

  if (style.pattern === 'herb') {
    // Własny herb: tarcza z krzyżem kotwicowym, w kolorze lamówki.
    const cx = W / 2;
    const cy = H * 0.32;
    g.fillStyle = trimHex;
    g.beginPath();
    g.moveTo(cx - 46, cy - 52);
    g.lineTo(cx + 46, cy - 52);
    g.lineTo(cx + 46, cy + 8);
    g.quadraticCurveTo(cx + 40, cy + 50, cx, cy + 70);
    g.quadraticCurveTo(cx - 40, cy + 50, cx - 46, cy + 8);
    g.closePath();
    g.fill();
    g.fillStyle = style.color;
    g.beginPath();
    g.moveTo(cx - 38, cy - 44);
    g.lineTo(cx + 38, cy - 44);
    g.lineTo(cx + 38, cy + 6);
    g.quadraticCurveTo(cx + 33, cy + 42, cx, cy + 60);
    g.quadraticCurveTo(cx - 33, cy + 42, cx - 38, cy + 6);
    g.closePath();
    g.fill();
    g.fillStyle = trimHex;
    g.fillRect(cx - 5, cy - 34, 10, 76);
    g.fillRect(cx - 26, cy - 12, 52, 10);
    g.beginPath();
    g.arc(cx, cy + 34, 14, 0, Math.PI);
    g.lineWidth = 6;
    g.strokeStyle = trimHex;
    g.stroke();
  }

  if (style.pattern === 'gwiazdy') {
    // Haft: drobne czteroramienne gwiazdy, gęstsze przy dole.
    g.fillStyle = trimHex;
    for (let n = 0; n < 70; n++) {
      const x = 14 + rnd() * (W - 28);
      const y = H * Math.sqrt(rnd());
      const s = 2 + rnd() * 5;
      g.globalAlpha = 0.55 + rnd() * 0.45;
      g.beginPath();
      g.moveTo(x, y - s * 2);
      g.lineTo(x + s * 0.45, y - s * 0.45);
      g.lineTo(x + s * 2, y);
      g.lineTo(x + s * 0.45, y + s * 0.45);
      g.lineTo(x, y + s * 2);
      g.lineTo(x - s * 0.45, y + s * 0.45);
      g.lineTo(x - s * 2, y);
      g.lineTo(x - s * 0.45, y - s * 0.45);
      g.closePath();
      g.fill();
    }
    g.globalAlpha = 1;
  }

  // Ciemniejsze plamy zużycia i przetarcia — tkanina, nie plastik.
  for (let n = 0; n < 40; n++) {
    const x = rnd() * W;
    const y = H * 0.3 + rnd() * H * 0.7;
    const r = 10 + rnd() * 30;
    const spot = g.createRadialGradient(x, y, 0, x, y, r);
    spot.addColorStop(0, 'rgba(0,0,0,0.12)');
    spot.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = spot;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }

  // Postrzępiony dół: wycinamy zęby w kanale alfa (osobne losowanie — podszewka ma ten sam brzeg co wierzch).
  const ragged = style.ragged ?? 0;
  seed = [...style.trim].reduce((s, ch) => s * 31 + ch.charCodeAt(0), 11) >>> 0;
  g.globalCompositeOperation = 'destination-out';
  g.fillStyle = '#000';
  g.beginPath();
  g.moveTo(0, H);
  let x = 0;
  while (x < W) {
    const w = ragged > 0 ? 10 + rnd() * 22 : W;
    const depth = ragged > 0 ? (0.25 + rnd() * rnd() * 1.6) * ragged * 110 : 4;
    g.lineTo(x + w * 0.5, H - depth);
    x += w;
    g.lineTo(Math.min(x, W), H - rnd() * 6);
  }
  g.lineTo(W, H);
  g.closePath();
  g.fill();
  g.globalCompositeOperation = 'source-over';

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
